import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { getApiEndpoints, getSelectedEnvironment } from '../utils/apiConfig';
import './SetupAgentPage.css';

const MAX_NOTIFICATION_CHARS = 250;

function normalizeUrl(url) {
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidHttpUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

const SetupSendNotificationPage = () => {
  const [message, setMessage] = useState('');
  const [shortLink, setShortLink] = useState('');
  const [priority, setPriority] = useState('HIGH');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const selectedEnvironment = getSelectedEnvironment();
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const SEND_NOTIFICATION_API = apiEndpoints.sendNotificationApi;

  const trimmedMessage = message.trim();
  const singleLineMessage = trimmedMessage.replace(/[\r\n]+/g, ' ');
  const normalizedShortLink = normalizeUrl(shortLink);
  const finalMessage = normalizedShortLink ? `${singleLineMessage} ${normalizedShortLink}` : singleLineMessage;
  const finalMessageLength = finalMessage.length;

  const submit = async (e) => {
    e.preventDefault();

    if (!trimmedMessage) {
      setError('Please enter a notification message.');
      return;
    }

    if (normalizedShortLink && !isValidHttpUrl(normalizedShortLink)) {
      setError('Please enter a valid short link (http or https).');
      return;
    }

    if (finalMessageLength > MAX_NOTIFICATION_CHARS) {
      setError(`Notification text must be ${MAX_NOTIFICATION_CHARS} characters or fewer, including the short link.`);
      return;
    }

    const parsedExpiresInDays = Number(expiresInDays);
    if (!Number.isInteger(parsedExpiresInDays) || parsedExpiresInDays < 1 || parsedExpiresInDays > 14) {
      setError('ExpiresInDays must be a whole number between 1 and 14.');
      return;
    }

    if (!SEND_NOTIFICATION_API) {
      setError('Send notification endpoint is not configured.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiClient.post(SEND_NOTIFICATION_API, {
        Action: 'create',
        Message: finalMessage,
        Priority: priority,
        ExpiresInDays: parsedExpiresInDays,
      });

      let bodyData = response.data;
      if (bodyData?.body && typeof bodyData.body === 'string') {
        try { bodyData = JSON.parse(bodyData.body); } catch (_) {}
      }

      setResult({
        message: bodyData?.message || 'Notification sent successfully.',
      });
      setMessage('');
      setShortLink('');
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.body && typeof errorData.body === 'string') {
        try {
          const parsed = JSON.parse(errorData.body);
          setError(parsed.message || parsed.error || 'Failed to send notification.');
          return;
        } catch (_) {}
      }

      setError(errorData?.message || errorData?.error || err.message || 'Failed to send notification.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Setup Send Notification</h1>
          <p className="page-subtitle">Type a notification message and send it directly.</p>
        </div>
        <Link to="/home" className="btn btn-secondary">← Back to Home</Link>
      </div>

      <div className="form-container">
        <div className="info-box" style={{ marginBottom: '1rem' }}>
          <h3>Notification Message</h3>
          <p>
            Enter plain text and an optional short link. Agents can click the link from the bell notification.
          </p>
        </div>

        <form onSubmit={submit} className="setup-form">
          <div className="form-section">
            <h3>Message</h3>
            <div className="form-group">
              <label htmlFor="notification_message">Notification Text *</label>
              <textarea
                id="notification_message"
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your notification here"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="notification_short_link">Short Link (optional)</label>
              <input
                id="notification_short_link"
                type="text"
                value={shortLink}
                onChange={(e) => setShortLink(e.target.value)}
                placeholder="go.example.com/alert-123 or https://go.example.com/alert-123"
                disabled={loading}
              />
            </div>

            <div className="form-group" style={{ marginTop: '-0.25rem' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: finalMessageLength > MAX_NOTIFICATION_CHARS ? '#b91c1c' : '#4b5563' }}>
                Characters used: {finalMessageLength}/{MAX_NOTIFICATION_CHARS}
              </p>
            </div>

            <div className="form-group">
              <label>Priority *</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  className={`btn ${priority === 'HIGH' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setPriority('HIGH')}
                  disabled={loading}
                >
                  HIGH
                </button>
                <button
                  type="button"
                  className={`btn ${priority === 'LOW' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setPriority('LOW')}
                  disabled={loading}
                >
                  LOW
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notification_expires_in_days">ExpiresInDays *</label>
              <input
                id="notification_expires_in_days"
                type="number"
                min="1"
                max="14"
                step="1"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                placeholder="7"
                disabled={loading}
                required
              />
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <h4>Error</h4>
              <p>{error}</p>
            </div>
          )}

          {result && (
            <div className="alert alert-success">
              <h4>✅ Notification Sent</h4>
              <p><strong>{result.message}</strong></p>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading || !message.trim()}>
              {loading ? 'Sending…' : 'Send Notification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetupSendNotificationPage;
